import React, { useEffect, useCallback } from "react";
import EditMenu from "../EditMenu";
import CategoryToolbar from "./components/CategoryToolbar";
import CategoryTable from "./components/CategoryTable";
import DeleteCategoryModal from "./components/DeleteCategoryModal";
import LoadingScreen from "../../../components/LoadingScreen";
import CategoryModal from "./components/category-modal/CategoryModal";
import { useCategories } from "./hooks/useCategories";
import { useCategoryActions } from "./hooks/useCategoryActions";
import { useCategoryPageState } from "./hooks/useCategoryPageState";
import { PageTitle } from "src/shared/PageTitle";
import { useUser } from "src/shared/context/UserContext";
import { pageTitle } from "src/shared/utils/brand";
import { useUserTheme } from "src/shared/utils/useUserTheme";

const CategoriesPage: React.FC = () => {
  const {user} = useUser();

  const { categories, loading: fetchLoading, totalRecords, fetchCategories } = useCategories();
  const { loading: actionLoading, createCategory, updateCategory, deleteCategory } = useCategoryActions();

  const {
    showDeletedCategories,
    setShowDeletedCategories,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchTerm,
    setSearchTerm,
    filteredTerm,
    isAddModalOpen,
    setIsAddModalOpen,
    isEditModalOpen,
    setIsEditModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    selectedCategory,
    setSelectedCategory,
  } = useCategoryPageState();

  const loading = fetchLoading || actionLoading;

  const businessType = user?.businessType ?? "";
  const { sidebarThemeClass } = useUserTheme();

  useEffect(() => {
    document.title = pageTitle("Categories › Settings");
  }, []);

  useEffect(() => {
    if (searchTerm) setPage(0);
  }, [searchTerm, setPage]);

  // Fetch categories whenever page, rowsPerPage, searchTerm, or showDeleted changes
  useEffect(() => {
    fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
  }, [showDeletedCategories, page, rowsPerPage, filteredTerm, fetchCategories]);

  const handleAddCategory = useCallback(
    async (categoryName: string, type: string) => {
     const response = await createCategory({
  categoryName: categoryName.toUpperCase(),
  type,
});

if (response) {
  setIsAddModalOpen(false);

  fetchCategories(
    showDeletedCategories,
    page,
    rowsPerPage,
    filteredTerm
  );
}
    },
    [createCategory, fetchCategories, showDeletedCategories, page, rowsPerPage, filteredTerm, setIsAddModalOpen]
  );

  const handleEditCategory = useCallback(
    async (categoryName: string, type: string) => {
      await updateCategory(selectedCategory.id, { categoryName: categoryName.toUpperCase(), ...(businessType === "Saloon" && { Type: type }) });
      setIsEditModalOpen(false);
      fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
    },
    [updateCategory, selectedCategory.id, businessType, fetchCategories, showDeletedCategories, page, rowsPerPage, filteredTerm, setIsEditModalOpen]
  );

  const handleDeleteCategory = useCallback(async () => {
    await deleteCategory(selectedCategory.id);
    setIsDeleteModalOpen(false);
    fetchCategories(showDeletedCategories, page, rowsPerPage, filteredTerm);
  }, [deleteCategory, selectedCategory.id, fetchCategories, showDeletedCategories, page, rowsPerPage, filteredTerm, setIsDeleteModalOpen]);

  const handleEditClick = (id: string, name: string) => {
    if (!showDeletedCategories) {
      const category = categories.find((cat) => cat._id.$oid === id);
      setSelectedCategory({ id, name, type: category?.type || "" });
      setIsEditModalOpen(true);
    }
  };

  const handleDeleteClick = (id: string, name: string) => {
    if (!showDeletedCategories) {
      setSelectedCategory({ id, name, type: "" });
      setIsDeleteModalOpen(true);
    }
  };

  const menu = { Category: "Category" };

  return (
    <div className="p-1">
    <div className={`flex flex-col w-full h-[91vh] shadow-inner rounded-lg items-center ${sidebarThemeClass} overflow-hidden`}>
      <PageTitle title='- Settings › Categories' />
      <EditMenu editmenu={menu} />

      <div className='flex w-[89vw] mt-[25px] mb-[15px]'>
        <CategoryToolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          showDeleted={showDeletedCategories}
          onShowDeletedChange={setShowDeletedCategories}
          onAddCategory={() => setIsAddModalOpen(true)}
        />
      </div>

      <CategoryTable
        categories={categories}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRecords={totalRecords}
        showDeleted={showDeletedCategories}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      />

      <CategoryModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSubmit={handleAddCategory} businessType={businessType} />

      <CategoryModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditCategory}
        initialName={selectedCategory.name}
        initialType={selectedCategory.type}
        isEdit
        businessType={businessType}
      />

      <DeleteCategoryModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteCategory}
        categoryName={selectedCategory.name}
      />

      {loading && <LoadingScreen />}
    </div>
    </div>
  );
};

export default CategoriesPage;